import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Calendar, Users, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SessionRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    designation: "",
    email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.organization.trim() || 
        !formData.designation.trim() || !formData.email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/session-registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Registration confirmed!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Registration failed. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Screen
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F2F7F4] to-white">
        {/* Header */}
        <header className="bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 py-4">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-[#1F7A5A] hover:text-[#145A43] transition-colors font-medium"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to CSR Partnership Page
            </button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#1F7A5A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#1F7A5A]" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#2F2F2F] mb-4">
              You're Registered!
            </h1>
            <p className="text-[#6B7280] mb-8 leading-relaxed">
              Thank you for registering for our Live CSR Ecosystem Session. We'll send you the session details and calendar invite to <span className="font-medium text-[#4A4A4A]">{formData.email}</span> shortly.
            </p>
            
            <Card className="p-6 bg-[#FFF8E6] border-0 text-left mb-8">
              <h3 className="font-semibold text-[#2F2F2F] mb-3">What to Expect:</h3>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1F7A5A] mt-0.5 flex-shrink-0" />
                  <span>Calendar invite with Zoom link within 24 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1F7A5A] mt-0.5 flex-shrink-0" />
                  <span>Pre-session briefing document with agenda</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1F7A5A] mt-0.5 flex-shrink-0" />
                  <span>Direct interaction with CLIC leadership team</span>
                </li>
              </ul>
            </Card>

            <Button
              onClick={() => navigate("/")}
              className="bg-[#1F7A5A] hover:bg-[#145A43] text-white"
              data-testid="return-home-btn"
            >
              Return to Partnership Page
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Registration Form
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2F7F4] to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#1F7A5A] hover:text-[#145A43] transition-colors font-medium"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to CSR Partnership Page
          </button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-2xl mx-auto">
          
          {/* Page Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#1F7A5A]/10 text-[#1F7A5A] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Calendar className="w-3.5 h-3.5" />
              Live Session
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#2F2F2F] mb-3">
              Join a Live CSR Ecosystem Session
            </h1>
            <p className="text-[#6B7280] max-w-md mx-auto">
              Register to join our interactive session with CSR practitioners, foundation leaders, and the CLIC team.
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <Card className="p-4 bg-white border border-gray-100 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#1F7A5A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#1F7A5A]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2F2F2F] text-sm mb-1">Peer Learning</h3>
                  <p className="text-xs text-[#6B7280]">Connect with CSR leaders from leading organizations</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white border border-gray-100 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#F4C430]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#B8860B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2F2F2F] text-sm mb-1">Monthly Sessions</h3>
                  <p className="text-xs text-[#6B7280]">Sessions held every month, 45-60 minutes each</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Registration Form Card */}
          <Card className="p-6 md:p-8 bg-white border-0 shadow-lg" data-testid="session-registration-form">
            <h2 className="font-display text-lg font-semibold text-[#2F2F2F] mb-6 pb-4 border-b border-gray-100">
              Register for Session
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-[#4A4A4A]">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="h-11 border-gray-200 focus:border-[#1F7A5A] focus:ring-[#1F7A5A]"
                  data-testid="name-input"
                />
              </div>

              {/* Organization */}
              <div className="space-y-2">
                <Label htmlFor="organization" className="text-sm font-medium text-[#4A4A4A]">
                  Organisation <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Company or foundation name"
                  className="h-11 border-gray-200 focus:border-[#1F7A5A] focus:ring-[#1F7A5A]"
                  data-testid="organization-input"
                />
              </div>

              {/* Designation */}
              <div className="space-y-2">
                <Label htmlFor="designation" className="text-sm font-medium text-[#4A4A4A]">
                  Designation <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="Your role or title"
                  className="h-11 border-gray-200 focus:border-[#1F7A5A] focus:ring-[#1F7A5A]"
                  data-testid="designation-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#4A4A4A]">
                  Official Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@company.com"
                  className="h-11 border-gray-200 focus:border-[#1F7A5A] focus:ring-[#1F7A5A]"
                  data-testid="email-input"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-[#1F7A5A] hover:bg-[#145A43] text-white font-medium text-base transition-colors"
                  data-testid="confirm-seat-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    "Confirm My Seat"
                  )}
                </Button>
              </div>

              <p className="text-xs text-center text-[#9CA3AF] pt-2">
                By registering, you'll receive session invites and CSR ecosystem updates from CLIC.
              </p>
            </form>
          </Card>

        </div>
      </main>
    </div>
  );
};

export default SessionRegistration;
